import React, { useMemo } from 'react';
import { ACCOUNT_TYPES, inferAccountType } from '../../constants';
import { formatCurrency, formatDate } from '../../utils';

const Dashboard = ({
    accounts,
    journals,
    getAccountBalance,
    currentFiscalYear,
    setCurrentPage,
    setFocusJournalId,
    creditCards,
    accountType
}) => {
    const isKojin = accountType === 'kojin';
    // 統計計算
    const stats = useMemo(() => {
        const totalRevenue = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE)
            .reduce((sum, a) => sum + getAccountBalance(a.id), 0);

        const totalExpense = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE)
            .reduce((sum, a) => sum + getAccountBalance(a.id), 0);

        const totalAssets = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.ASSET)
            .reduce((sum, a) => sum + getAccountBalance(a.id), 0);

        const totalLiabilities = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.LIABILITY)
            .reduce((sum, a) => sum + getAccountBalance(a.id), 0);

        return {
            totalRevenue,
            totalExpense,
            profit: totalRevenue - totalExpense,
            totalAssets,
            totalLiabilities,
            journalCount: journals.length
        };
    }, [accounts, journals, getAccountBalance]);

    // カード残高計算
    const cardSummaries = useMemo(() => {
        if (!creditCards || creditCards.length === 0) return [];

        return creditCards.map(card => {
            // カード付き未払金（貸方）
            const totalUnpaid = journals
                .filter(j => !j.deletedAt)
                .flatMap(j => j.lines)
                .filter(l => l.type === 'credit' && l.creditCardId === card.id)
                .reduce((s, l) => s + l.amount, 0);

            // 消込済み
            const totalSettled = journals
                .filter(j => !j.deletedAt)
                .filter(j =>
                    j.lines.some(l => l.type === 'debit' && l.accountId === card.unpaidAccountId) &&
                    j.lines.some(l => l.type === 'credit' && l.accountId === card.paymentAccountId)
                )
                .flatMap(j => j.lines)
                .filter(l => l.type === 'debit' && l.accountId === card.unpaidAccountId)
                .reduce((s, l) => s + l.amount, 0);

            const balance = totalUnpaid - totalSettled + (card.openingBalance || 0);
            const hasUnsettled = totalUnpaid > totalSettled;

            return { card, totalUnpaid, totalSettled, balance, hasUnsettled };
        }).filter(s => s.totalUnpaid > 0 || s.balance > 0);
    }, [creditCards, journals]);

    const recentJournals = useMemo(() => {
        return [...journals]
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 10);
    }, [journals]);

    return (
        <div>
            <div className="page-header">
                <h2>ダッシュボード <span className="year-badge">{currentFiscalYear}年度</span></h2>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <h3>収益合計</h3>
                    <div className="value" style={{color: '#27ae60'}}>
                        ¥{formatCurrency(stats.totalRevenue)}
                    </div>
                </div>
                <div className="stat-card">
                    <h3>費用合計</h3>
                    <div className="value" style={{color: '#e74c3c'}}>
                        ¥{formatCurrency(stats.totalExpense)}
                    </div>
                </div>
                <div className="stat-card">
                    <h3>{isKojin ? '当期損益' : '当期正味財産増減額'}</h3>
                    <div className="value" style={{color: stats.profit >= 0 ? '#27ae60' : '#e74c3c'}}>
                        ¥{formatCurrency(stats.profit)}
                    </div>
                </div>
                <div className="stat-card">
                    <h3>仕訳件数</h3>
                    <div className="value">
                        {stats.journalCount}件
                    </div>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <h3>資産合計</h3>
                    <div className="value">
                        ¥{formatCurrency(stats.totalAssets)}
                    </div>
                </div>
                <div className="stat-card">
                    <h3>負債合計</h3>
                    <div className="value">
                        ¥{formatCurrency(stats.totalLiabilities)}
                    </div>
                </div>
            </div>

            {cardSummaries.length > 0 && (
                <div className="card" style={{ cursor: 'pointer' }} onClick={() => setCurrentPage('card-settlement')}>
                    <h3 className="card-title">
                        クレジットカード残高
                        {cardSummaries.some(s => s.hasUnsettled) && (
                            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#e67e22', fontWeight: 'normal' }}>
                                未消込あり
                            </span>
                        )}
                    </h3>
                    <table>
                        <thead>
                            <tr>
                                <th>カード名</th>
                                <th className="text-right">利用総額</th>
                                <th className="text-right">消込済み</th>
                                <th className="text-right">未払残高</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cardSummaries.map(s => (
                                <tr key={s.card.id}>
                                    <td>
                                        {s.card.name}
                                        {s.hasUnsettled && <span style={{ color: '#e67e22', marginLeft: '4px' }}>*</span>}
                                    </td>
                                    <td className="text-right amount">¥{formatCurrency(s.totalUnpaid)}</td>
                                    <td className="text-right amount">¥{formatCurrency(s.totalSettled)}</td>
                                    <td className="text-right amount" style={{ color: s.balance > 0 ? '#e67e22' : '#27ae60' }}>
                                        ¥{formatCurrency(s.balance)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="card">
                <h3 className="card-title">最近の仕訳</h3>
                {recentJournals.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th>日付</th>
                                <th>摘要</th>
                                <th className="text-right">借方金額</th>
                                <th className="text-right">貸方金額</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentJournals.map(journal => {
                                const debitTotal = journal.lines
                                    .filter(l => l.type === 'debit')
                                    .reduce((sum, l) => sum + l.amount, 0);
                                const creditTotal = journal.lines
                                    .filter(l => l.type === 'credit')
                                    .reduce((sum, l) => sum + l.amount, 0);
                                return (
                                    <tr
                                        key={journal.id}
                                        onClick={() => { setFocusJournalId(journal.id); setCurrentPage('journals'); }}
                                        style={{cursor: 'pointer'}}
                                        title="クリックで仕訳を開く"
                                    >
                                        <td>{formatDate(journal.date)}</td>
                                        <td>{journal.description}</td>
                                        <td className="text-right amount">¥{formatCurrency(debitTotal)}</td>
                                        <td className="text-right amount">¥{formatCurrency(creditTotal)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <div className="empty-state">
                        <h3>仕訳がありません</h3>
                        <p>「仕訳入力」から新しい仕訳を登録してください</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
